import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import { hasRole, type Role } from '@conversia/shared-types';
import { useMenu } from '@/hooks/useMenu';

/**
 * Guarda de rutas: si no hay sesión, redirige a /login conservando el destino.
 * Si hay token pero aún no se cargó el usuario, dispara `loadMe`.
 * `requireRole` exige un rol mínimo (redirige a / si no alcanza).
 * `requireRuta` exige que el servidor le dé esa pantalla a quien mira (7-oct: «Sistema», el
 * gasto, los topes y la clave de IA son de la administración; el rol no alcanza para saberlo).
 */
export function ProtectedRoute({
  children,
  requireRole,
  requireRuta,
}: {
  children: React.ReactNode;
  requireRole?: Role;
  requireRuta?: string;
}) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const loadMe = useAuthStore((s) => s.loadMe);
  const location = useLocation();
  const menu = useMenu();

  useEffect(() => {
    if (accessToken && !user) void loadMe();
  }, [accessToken, user, loadMe]);

  if (!accessToken) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (requireRole && user && !hasRole(user.role, requireRole)) {
    return <Navigate to="/" replace />;
  }
  if (requireRuta) {
    // Mientras el servidor contesta no se dibuja nada: dibujar la pantalla un instante ya le
    // pedía sus datos a la API (un 403 a la vista) antes de echar a quien no le toca.
    if (menu.cargando) return null;
    // Si el menú falló no se echa a nadie: la API igual responde 403.
    if (menu.datos && !menu.datos.rutas.some((r) => r.ruta === requireRuta)) return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
